/**
 * The dashboard's wiring for an attribution write.
 *
 * What a write MEANS — which route carries which claim, which attributes a
 * pairing can carry, whether the candidate may land on the subject at all — is
 * `createAttributionWriter` in `@authe/fe-commons/attribution`, shared with the
 * admin portal. What is left here is the half that is genuinely this app's:
 *
 *   - which React Query caches a write disturbs, and the response-cache sweep
 *     for the readers that are not React Query at all;
 *   - the caller's per-kind visibility default, raised in a shared case, with
 *     their team ids attached;
 *   - a counted busy flag, so a bulk bar's concurrent writes don't have the first
 *     one to finish clear it while the rest are still going.
 *
 * The split is not cosmetic: the invalidation below is a list of keys that only
 * exist in this app, and the routing above is a list of rules that must not
 * differ between apps.
 */
import * as React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "@authe/ui-core/api";
import {
  createAttributionWriter,
  effectiveWriteScope,
  type AttributionKind,
  type AttributionSubject,
  type AttributionTarget,
  type AttributionVisibility,
  type AttributionWrite,
} from "@authe/fe-commons/attribution";
import type { EntityMappingInput } from "@authe/fe-commons/addresses";
import { useAddressesApi } from "@/api/adapters";
import { useScopePrefs } from "@/app/scopePrefs";
import { useMyTeams } from "@/app/useMyTeams";
import { useInvalidateAddressOverlays } from "@/features/overlays";

export interface UseAttributionControllerOptions {
  /**
   * True when the result is visible to the whole tenant (a Shared case file).
   * Raises a `private` visibility default so teammates can see the work.
   */
  shared?: boolean;
  /**
   * Called after a write lands. The caches this controller knows about are
   * invalidated for you; this is for the ones it cannot know — a transaction's
   * `catalog` block also rides on the feed row, and the feed is not a React
   * Query cache.
   */
  onChanged?: () => void;
}

export interface AttributionControllerApi {
  assign(write: AttributionWrite): Promise<void>;
  unassign(
    subject: AttributionSubject,
    kind: AttributionKind,
    catalogId: string,
    target?: AttributionTarget,
  ): Promise<void>;
  /** Upsert the authoritative `(chain, address) → entity` membership row. */
  setMembership(subject: AttributionSubject, input: EntityMappingInput): Promise<void>;
  /** The visibility a write would use right now — for a form's initial value. */
  defaultVisibility(kind: AttributionKind): AttributionVisibility;
  /** True while any write is in flight (drives disabled/busy states). */
  isBusy: boolean;
}

export function useAttributionController(
  options: UseAttributionControllerOptions = {},
): AttributionControllerApi {
  const { shared = false, onChanged } = options;
  const addresses = useAddressesApi();
  const client = useApiClient();
  const queryClient = useQueryClient();
  const { resolveDefaultWriteScope } = useScopePrefs();
  // The ungated memberships, not the streaming ones: a team-scoped attach from
  // a page with no live transport must still name the teams it shares with.
  const teams = useMyTeams();
  const teamIds = React.useMemo(() => teams.map((t) => t.id), [teams]);
  const [inFlight, setInFlight] = React.useState(0);
  // The shared address invalidation — one list, used by every writer of an
  // address row. See `useInvalidateAddressOverlays`.
  const invalidateAddresses = useInvalidateAddressOverlays();

  const defaultVisibility = React.useCallback(
    (kind: AttributionKind): AttributionVisibility => ({
      scope: effectiveWriteScope(resolveDefaultWriteScope(kind), { shared }),
      teamIds,
    }),
    [resolveDefaultWriteScope, shared, teamIds],
  );

  const writer = React.useMemo(() => {
    const drop = (key: string): void => {
      void queryClient.invalidateQueries({ queryKey: [key] });
    };

    // The address reads all opt out of the client's response cache, so the
    // React Query keys are the whole story here — unlike the transaction side.
    //
    // Every address-derived cache comes from `ADDRESS_QUERY_PREFIXES`, the ONE
    // list, rather than being re-spelled here. The RESOLVED bundles are keyed by
    // the whole ref list (batch) or by `(address, chain, scope)` (single), so
    // there is no one entry to touch — the list is prefixes and the mounted
    // panels refetch. Keeping a second copy here is exactly how `address-overlays`
    // came to be in one of them and not the other.
    const refreshAddress = (): void => {
      invalidateAddresses();
      // The entity page's lanes are keyed by the ENTITY, not by the address, so
      // a membership write against one address has no key of its own to touch —
      // and the matrix it just changed is the surface most likely to be on
      // screen when it happens. Not in the address list, because these are not
      // caches of an address.
      drop("entity-memberships");
      drop("catalog-addresses-attached");
      drop("catalog-addresses-headline");
    };

    // A transaction's attribution is resolved by its own batch endpoint, over a
    // different subject, so an address invalidation does not reach it.
    //
    // The response-cache sweep is for the OTHER readers of this transaction — a
    // list page holding a cached `/transactions` GET that shows its chips. The
    // inspector's own detail read opts out of that cache entirely
    // (`useTransactionDetail`), because invalidating by canonical id cannot
    // reach a read keyed by `chain:hash` and cannot outrun a GET that was
    // already in flight when the write landed.
    const refreshTransaction = (canonicalTxId: string): void => {
      client.invalidateMatching(`/transactions/${encodeURIComponent(canonicalTxId)}`);
      drop("tx-catalog-batch");
      drop("tx-tenant-triage");
    };

    return createAttributionWriter({
      addresses,
      patchTransaction: async (canonicalTxId, body) => {
        const res = await client.patch<unknown>(
          `/transactions/${encodeURIComponent(canonicalTxId)}`,
          body,
          { query: { scope: "tenant" }, retries: 0, cacheTtlMs: 0 },
        );
        if (!res.ok) throw new Error(res.error.message || "Could not save the attribution");
      },
      invalidate: (subject) => {
        if (subject.kind === "address") refreshAddress();
        else if (subject.kind === "transaction") refreshTransaction(subject.canonicalTxId);
        else drop("address-overlays-batch");
        onChanged?.();
      },
      defaultVisibility,
    });
  }, [addresses, client, defaultVisibility, invalidateAddresses, onChanged, queryClient]);

  // Counted rather than a boolean, per the module docstring.
  const track = React.useCallback(async (run: () => Promise<void>): Promise<void> => {
    setInFlight((n) => n + 1);
    try {
      await run();
    } finally {
      setInFlight((n) => n - 1);
    }
  }, []);

  return React.useMemo<AttributionControllerApi>(
    () => ({
      assign: (write) => track(() => writer.assign(write)),
      unassign: (subject, kind, catalogId, target) =>
        track(() => writer.unassign(subject, kind, catalogId, target)),
      setMembership: (subject, input) => track(() => writer.setMembership(subject, input)),
      defaultVisibility,
      isBusy: inFlight > 0,
    }),
    [defaultVisibility, inFlight, track, writer],
  );
}
